import { useCallback, useState } from 'react';
import {
  MfaSecurityKeyAuthorizeError,
  MfaSecurityKeyChallengeError,
  MfaSecurityKeyChallengeResponse,
  MfaSecurityKeyVerifyError,
  URLParameters,
} from '../../../types';
import { get, parseRequestOptionsFromJSON } from '@github/webauthn-json/browser-ponyfill';
import {
  AnalyticsClient,
  useAnalyticsClient,
} from '../../../utilities/analytics/analytics-web-client';
import queryString from 'query-string';
import {
  WebAuthnAuthenticationErrorCode,
  isWebAuthnSupported,
  translateAuthenticationError,
} from '../../../helpers/webauthn';
import { PublicKeyCredentialWithAssertionJSON } from '@github/webauthn-json';
import { captureErrorMessage } from '../../../utilities/analytics/error-reporting';
import { PublicKeyCredentialRequestOptionsJSON } from '@github/webauthn-json/dist/types/basic/json';

const ANALYTIC_EVENT_SUBJECT_ID = 'challengeSecurityKey';

export enum ChallengeApiError {
  transactionTokenNotFound = 'transactionTokenNotFound',
  challengeFailure = 'challengeFailure',
  tooManyRequests = 'tooManyRequests',
}
export type ChallengeError = ChallengeApiError | WebAuthnAuthenticationErrorCode;

type ChallengeProps = {
  transactionToken: string;
};

type Result<T, E> = { success: true; data: T } | { success: false; error: E };
type MfaSecurityKeyChallengeResponseResult = Result<
  MfaSecurityKeyChallengeResponse,
  MfaSecurityKeyChallengeError
>;

const challenge = async ({
  transactionToken,
}: ChallengeProps): Promise<MfaSecurityKeyChallengeResponseResult> => {
  try {
    const response = await fetch(
      `/v2/mfa/security-key/challenge?transactionToken=${transactionToken}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      }
    );
    switch (response.status) {
      case 200:
        const challengeResponse = (await response.json()) as MfaSecurityKeyChallengeResponse;
        return { success: true, data: challengeResponse };
      case 429:
        return { success: false, error: 'too_many_requests' };
      default:
        throw response;
    }
  } catch (error) {
    throw new Error(`error from fetch: /v2/mfa/security-key/challenge`);
  }
};

type AuthorizeProps = {
  transactionToken: string;
};

type AuthorizeResponse = {
  redirectUri: URL;
};

type MfaSecurityKeyAuthorizeResponseResult = Result<
  AuthorizeResponse,
  MfaSecurityKeyAuthorizeError
>;

const authorize = async ({
  transactionToken,
}: AuthorizeProps): Promise<MfaSecurityKeyAuthorizeResponseResult> => {
  try {
    const queryParams: URLParameters = queryString.parse(window.location.search);
    queryParams.transactionToken = transactionToken;
    const response = await fetch(`/rest/mfa/authorize?${queryString.stringify(queryParams)}`);
    switch (response.status) {
      case 200:
        const authorizeResponse = (await response.json()) as AuthorizeResponse;
        return { success: true, data: authorizeResponse };
      case 429:
        return { success: false, error: 'too_many_requests' };
      default:
        throw response;
    }
  } catch (error) {
    throw new Error(`error from fetch: /rest/mfa/authorize`);
  }
};

type MfaSecurityKeyAuthenticatorChallengeResult = Result<
  PublicKeyCredentialWithAssertionJSON,
  WebAuthnAuthenticationErrorCode
>;

const challengeWithAuthenticator = async (
  requestOptions: PublicKeyCredentialRequestOptionsJSON
): Promise<MfaSecurityKeyAuthenticatorChallengeResult> => {
  try {
    const options = parseRequestOptionsFromJSON({
      publicKey: {
        ...requestOptions,
        timeout: 60000,
      },
    });
    try {
      const securityKeyCredential = await get(options);
      return { success: true, data: securityKeyCredential.toJSON() };
    } catch (error) {
      captureErrorMessage('Security key authentication failed', {
        webauthnError: { name: error.name, message: JSON.stringify(error.message) },
      });
      const errorCode = translateAuthenticationError({ error: error, options });
      return { success: false, error: errorCode };
    }
  } catch (error) {
    captureErrorMessage('Failed to parse authenticate options for security key', {
      webauthnError: { name: error.name, message: JSON.stringify(error.message) },
    });
    return { success: false, error: WebAuthnAuthenticationErrorCode.InvalidRequestOptions };
  }
};

type VerifyProps = {
  transactionToken: string;
  assertionJson: PublicKeyCredentialWithAssertionJSON;
  analyticsClient: AnalyticsClient;
  pageId: string;
};

type VerifyResponse = {
  redirectUri: URL;
};

type VerifyAPIResponse = {
  transactionToken: string;
};
type MfaSecurityKeyVerifyResponseResult = Result<VerifyResponse, MfaSecurityKeyVerifyError>;

const verify = async ({
  transactionToken,
  assertionJson,
  analyticsClient,
  pageId,
}: VerifyProps): Promise<MfaSecurityKeyVerifyResponseResult> => {
  try {
    const clientExtensionResultsJsonString = JSON.stringify(assertionJson.clientExtensionResults);
    const response = await fetch(
      `/v2/mfa/security-key/verify?transactionToken=${transactionToken}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...assertionJson,
          clientExtensionResults: clientExtensionResultsJsonString,
        }),
      }
    );

    switch (response.status) {
      case 200: {
        const { transactionToken } = (await response.json()) as VerifyAPIResponse;
        const verifyResponseResult = (await authorize({
          transactionToken,
        })) as MfaSecurityKeyVerifyResponseResult;
        if (!verifyResponseResult.success) {
          switch (verifyResponseResult.error) {
            case 'too_many_requests':
              return { success: false, error: 'too_many_requests' };
            default:
              throw new Error('error during verification of security key');
          }
        }
        analyticsClient.trackingEvent({
          page: pageId,
          action: 'succeeded',
          subject: ANALYTIC_EVENT_SUBJECT_ID,
        });
        return { success: true, data: verifyResponseResult.data as VerifyResponse };
      }
      case 400: {
        const queryParams: URLParameters = queryString.parse(window.location.search);
        const redirectUri = redirectToLoginErrorOn400IncorrectTokenResponse({
          analyticsClient,
          pageId,
          currentQueryParams: queryParams,
        });
        return { success: true, data: { redirectUri } };
      }
      case 429:
        return { success: false, error: 'too_many_requests' };
      default:
        throw response;
    }
  } catch (error) {
    throw new Error(`error during verification of security key`);
  }
};

export const useChallengeSecurityKey = (
  pageId: string,
  onSuccess: (responseData: VerifyResponse) => void,
  onError: (error: ChallengeError) => void
) => {
  const params = new URLSearchParams(window.location.search);
  const [transactionToken, setTransactionToken] = useState<string | null>(
    params.get('transactionToken')
  );

  const isSupported = isWebAuthnSupported();
  const analyticsClient = useAnalyticsClient();
  const [authenticating, setAuthenticating] = useState(false);

  const onErrorWithTrackingAnalytics = useCallback(
    (error: ChallengeError) => {
      analyticsClient.trackingEvent({
        page: pageId,
        action: 'failed',
        subject: ANALYTIC_EVENT_SUBJECT_ID,
        attributes: { reason: error },
      });
      onError(error);
    },
    [analyticsClient, onError, pageId]
  );

  const challengeSecurityKey = useCallback(async () => {
    analyticsClient.trackingEvent({
      page: pageId,
      action: 'started',
      subject: ANALYTIC_EVENT_SUBJECT_ID,
    });
    if (!isSupported) {
      return onErrorWithTrackingAnalytics(WebAuthnAuthenticationErrorCode.WebAuthnNotSupported);
    }

    if (!transactionToken) {
      return onErrorWithTrackingAnalytics(ChallengeApiError.transactionTokenNotFound);
    }

    try {
      setAuthenticating(true);
      const securityKeyChallengeResponseResult = await challenge({ transactionToken });

      if (!securityKeyChallengeResponseResult.success) {
        setAuthenticating(false);
        switch (securityKeyChallengeResponseResult.error) {
          case 'too_many_requests':
            return onErrorWithTrackingAnalytics(ChallengeApiError.tooManyRequests);
          default:
            return onErrorWithTrackingAnalytics(ChallengeApiError.challengeFailure);
        }
      }
      setTransactionToken(securityKeyChallengeResponseResult.data.transactionToken);

      const securityKeyChallengeResponse = securityKeyChallengeResponseResult.data;
      const authenticatorChallengeResult = await challengeWithAuthenticator(
        securityKeyChallengeResponse.requestOptions
      );
      if (!authenticatorChallengeResult.success) {
        setAuthenticating(false);
        return onErrorWithTrackingAnalytics(authenticatorChallengeResult.error);
      }
      const securityKeyCredentialJson = authenticatorChallengeResult.data;

      const verifyResponseResult = await verify({
        transactionToken: securityKeyChallengeResponse.transactionToken,
        assertionJson: securityKeyCredentialJson,
        analyticsClient: analyticsClient,
        pageId,
      });
      if (!verifyResponseResult.success) {
        setAuthenticating(false);
        switch (verifyResponseResult.error) {
          case 'too_many_requests':
            return onErrorWithTrackingAnalytics(ChallengeApiError.tooManyRequests);
          default:
            return onErrorWithTrackingAnalytics(ChallengeApiError.challengeFailure);
        }
      }

      return onSuccess(verifyResponseResult.data);
    } catch (e) {
      setAuthenticating(false);
      return onErrorWithTrackingAnalytics(ChallengeApiError.challengeFailure);
    }
  }, [
    isSupported,
    analyticsClient,
    transactionToken,
    onSuccess,
    pageId,
    onErrorWithTrackingAnalytics,
  ]);

  return { challengeSecurityKey, authenticating };
};

function redirectToLoginErrorOn400IncorrectTokenResponse({
  analyticsClient,
  pageId,
  currentQueryParams,
}: {
  analyticsClient: AnalyticsClient;
  pageId: string;
  currentQueryParams: URLParameters;
}): URL {
  analyticsClient.trackingEvent({
    page: pageId,
    subject: ANALYTIC_EVENT_SUBJECT_ID,
    action: 'failed',
    attributes: { reason: 'login_redirect_invalid_token' },
  });
  delete currentQueryParams.transactionToken;
  const currentQueryString = queryString.stringify(currentQueryParams);
  const extraParamsString = currentQueryString === '' ? '' : `&${currentQueryString}`;

  return new URL(
    `/login?prompt=true&errorCode=login.form.token.expired${extraParamsString}`,
    window.location.origin
  );
}
